import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, MapPin, Clock, ArrowRight, Search, Users, TrendingUp, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const openings = [
  { id: 1, title: 'Senior AI/ML Instructor', dept: 'Academics', location: 'Bangalore / Remote', type: 'Full-time', exp: '5+ years', desc: 'Lead our GenAI and AIML courses, develop curriculum, and mentor students through projects.' },
  { id: 2, title: 'Cybersecurity Trainer (VAPT)', dept: 'Academics', location: 'Bangalore / Hyderabad', type: 'Full-time', exp: '4+ years', desc: 'Deliver VAPT and SOC training, conduct live labs, and keep curriculum updated with latest threats.' },
  { id: 3, title: 'Full Stack Development Instructor', dept: 'Academics', location: 'Remote', type: 'Full-time / Part-time', exp: '3+ years', desc: 'Teach React, Node.js, and MongoDB. Build projects with students and provide code reviews.' },
  { id: 4, title: 'Student Success Manager', dept: 'Operations', location: 'Bangalore', type: 'Full-time', exp: '2+ years', desc: 'Guide students through their learning journey, track progress, and coordinate placement activities.' },
  { id: 5, title: 'Placement Coordinator', dept: 'Placements', location: 'Bangalore / Mumbai', type: 'Full-time', exp: '2+ years', desc: 'Build relationships with hiring partners, coordinate interviews, and support student job searches.' },
  { id: 6, title: 'Content Developer – Tech Courses', dept: 'Content', location: 'Remote', type: 'Contract', exp: '2+ years', desc: 'Create engaging course content, assessments, and video scripts for our tech programs.' },
];

const perks = [
 { icon: TrendingUp, title: 'Growth Fast Track', desc: 'Clear career progression and skill development budget' },
{ icon: Users, title: 'Collaborative Culture', desc: 'Work with passionate educators and technologists' },
{ icon: Briefcase, title: 'Flexible Work', desc: 'Hybrid and remote options for most roles' },
{ icon: BookOpen, title: 'Learning Support', desc: 'Access to courses, workshops, and continuous learning resources' },
];

export default function Careers() {
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState(null);

  const filtered = openings.filter(j =>
    j.title.toLowerCase().includes(search.toLowerCase()) ||
    j.dept.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary/10 via-background to-accent/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">Join Our Team</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Build India's <span className="text-primary">Tech Future</span><br />With Us
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
              We're on a mission to make world-class tech education accessible to every Indian learner. Join us and make an impact.
            </p>
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input className="pl-10 h-12" placeholder="Search roles..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-12 bg-card border-y">
        <div className="max-w-5xl mx-auto px-4 grid sm:grid-cols-2 md:grid-cols-4 gap-6">
          {perks.map((perk, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <perk.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-semibold mb-1">{perk.title}</h3>
              <p className="text-sm text-muted-foreground">{perk.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl font-bold">Open Positions</h2>
            <span className="text-sm text-muted-foreground">{filtered.length} roles available</span>
          </div>

          {applied ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20 bg-card rounded-2xl border">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">Application Submitted!</h3>
              <p className="text-muted-foreground mb-2">You've applied for <strong>{applied}</strong></p>
              <p className="text-sm text-muted-foreground mb-6">Our HR team will review and reach out within 5 business days.</p>
              <Button variant="outline" onClick={() => setApplied(null)}>View More Roles</Button>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {filtered.map((job, i) => (
                <motion.div key={job.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="bg-card rounded-xl border p-6 hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-heading font-semibold text-lg">{job.title}</h3>
                        <Badge variant="secondary">{job.dept}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{job.desc}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{job.location}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{job.type}</span>
                        <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{job.exp}</span>
                      </div>
                    </div>
                    <Button onClick={() => setApplied(job.title)} className="shrink-0">
                      Apply Now <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </motion.div>
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">No roles match your search.</div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}